import { applyMiddleware, compose, createStore as reduxCreateStore, Store } from 'redux'
import thunk, { ThunkDispatch, ThunkAction } from 'redux-thunk'
import { ElectionApi } from '../api'
import { rootReducer } from './reducers'
import { AppState } from './state'
import { defaultFylkesresultat } from './reducers/fylkesresultat'
import { defaultKommuneresultat } from './reducers/kommuneresultat'
import { AppAction } from './actions'
import { defaultGrunndata } from './reducers/grunndata'
import { defaultPage } from './reducers/page'
import { name } from '../../../package.json'

export type AppStore = Store<AppState, AppAction> & {
  dispatch: ThunkDispatch<AppState, ElectionApi, AppAction>
}

export type AppThunkAction<R = Promise<any>> = ThunkAction<R, AppState, ElectionApi, AppAction>

function createErrorLoggingMiddleware(logger) {
  return () => next => action => {
    if (action.type && action.type.endsWith('FAILURE')) {
      const error = action.payload.error || action.payload

      logger.error(error, `Async action failed: ${action.type}`)
    }

    try {
      return next(action)
    } catch (err) {
      logger.error(err)
      throw err
    }
  }
}

const defaultState = {
  page: defaultPage,
  fylkesresultat: defaultFylkesresultat,
  kommuneresultat: defaultKommuneresultat,
  grunndata: defaultGrunndata
}

export function createStore(
  api: ElectionApi,
  logger,
  initialState: AppState = defaultState
): AppStore {
  const composeEnhancers =
    typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
          name
        })
      : compose

  const enhancer = composeEnhancers(
    applyMiddleware(createErrorLoggingMiddleware(logger), thunk.withExtraArgument<ElectionApi>(api))
  )

  return reduxCreateStore(rootReducer, initialState, enhancer)
}

export function observeStore<R>(store: AppStore, select: (AppState) => R, onChange: (R) => void) {
  let currentState

  function handleChange() {
    const nextState = select(store.getState())
    if (nextState !== currentState) {
      currentState = nextState
      onChange(currentState)
    }
  }

  const unsubscribe = store.subscribe(handleChange)
  handleChange()
  return unsubscribe
}
